import { useState } from 'react';
import type { ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CalendarPlus } from 'lucide-react';
import { Button, Card, TextField } from '@tappa/ui';
import { ApiError, api } from '../../lib/api.js';

/**
 * Attività decisa da te, a un'ora precisa.
 *
 * Serve per tutto ciò che il planner non può sapere: la cena con un amico alle
 * otto, il volo di ritorno, la visita guidata già prenotata, il riposo del
 * pomeriggio.
 *
 * Viene inserita **bloccata**: il planner non la sposta mai e costruisce il
 * resto della giornata attorno a lei. È l'unico comportamento sensato — se ti
 * hanno dato appuntamento alle 20, non è una preferenza da ottimizzare.
 */
export function AddActivity({
  tripId,
  tripDayId,
  dataGiornata,
  timezone,
}: {
  tripId: string;
  tripDayId: string;
  /** Data della giornata, formato `AAAA-MM-GG`. */
  dataGiornata: string;
  timezone: string;
}): ReactNode {
  const queryClient = useQueryClient();
  const [aperto, setAperto] = useState(false);
  const [titolo, setTitolo] = useState('');
  const [ora, setOra] = useState('20:00');
  const [durata, setDurata] = useState('60');
  const [errore, setErrore] = useState<string | null>(null);
  const [dove, setDove] = useState('');
  const [posizione, setPosizione] = useState<{ lat: number; lon: number; label: string } | null>(
    null,
  );
  const [avviso, setAvviso] = useState<string | null>(null);

  /*
   * Trova il posto: da un indirizzo o da un link di Google Maps.
   *
   * Serve perche' una tappa senza coordinate non compare sulla mappa e non
   * entra nel calcolo degli spostamenti — resta una riga di testo. E se il
   * posto e' lontano dal resto della giornata, meglio saperlo adesso che
   * scoprirlo con un'ora di tragitto imprevisto.
   */
  const cerca = useMutation({
    mutationFn: () => api.days.resolvePlace(tripId, tripDayId, dove.trim()),
    onSuccess: (risposta) => {
      setPosizione(risposta.place);
      setAvviso(risposta.message);
      setErrore(null);
      if (titolo.trim() === '') setTitolo(risposta.place.label.split(',')[0] ?? '');
    },
    onError: (error) => {
      setPosizione(null);
      setAvviso(null);
      setErrore(error instanceof ApiError ? error.message : 'Indirizzo non trovato.');
    },
  });

  /*
   * Salvataggio.
   *
   * Se c'e' un indirizzo scritto ma non ancora cercato, lo si cerca adesso
   * invece di ignorarlo: prima questo campo veniva **buttato via** in silenzio
   * per chi non premeva «Trova sulla mappa», e la tappa nasceva senza
   * posizione — invisibile sulla mappa e fuori dal calcolo degli spostamenti,
   * senza che niente lo segnalasse.
   */
  const crea = useMutation({
    mutationFn: async () => {
      let punto = posizione;
      if (punto === null && dove.trim().length >= 3) {
        const risposta = await api.days.resolvePlace(tripId, tripDayId, dove.trim());
        punto = risposta.place;
        setPosizione(risposta.place);
      }
      return await api.items.create(tripId, {
        tripDayId,
        kind: 'reservation',
        title: titolo.trim(),
        // L'ora scelta va interpretata nel fuso della destinazione, non in
        // quello del telefono: alle 20 a Chicago, non alle 20 a casa.
        plannedStart: oraLocaleInIstante(dataGiornata, ora, timezone),
        durationMin: Number(durata),
        isLocked: true,
        ...(punto === null ? {} : { lat: punto.lat, lon: punto.lon }),
      });
    },
    onSuccess: async () => {
      setErrore(null);
      setTitolo('');
      setDove('');
      setPosizione(null);
      setAvviso(null);
      setAperto(false);
      await queryClient.invalidateQueries({ queryKey: ['day'] });
      await queryClient.invalidateQueries({ queryKey: ['today'] });
    },
    onError: (error) => {
      // Distinguere i due fallimenti: se l'indirizzo non esiste, dirlo — e non
      // salvare una tappa monca facendo finta di niente.
      setErrore(
        error instanceof ApiError
          ? `${error.message} Correggi «Dove», oppure svuotalo per aggiungerla senza posizione.`
          : 'Non sono riuscito ad aggiungerla.',
      );
    },
  });

  if (!aperto) {
    return (
      <Button
        variant="secondary"
        iconLeft={<CalendarPlus size={17} />}
        onClick={() => {
          setAperto(true);
        }}
      >
        Aggiungi una tua attività
      </Button>
    );
  }

  const valido = titolo.trim() !== '' && ora !== '' && Number(durata) >= 5;

  return (
    <Card>
      <h3 className="mb-1 font-display text-[17px] text-text">Una tua attività</h3>
      <p className="mb-3 text-[13.5px] leading-5 text-text-muted">
        Resta fissata all’orario che scegli: il resto della giornata verrà costruito attorno.
      </p>

      <div className="flex flex-col gap-3">
        <TextField
          label="Cosa"
          placeholder="Cena con Luca, volo di ritorno, visita guidata…"
          value={titolo}
          onChange={(event) => {
            setTitolo(event.target.value);
          }}
        />

        <div>
          <TextField
            label="Dove"
            placeholder="Indirizzo, oppure incolla il link di Google Maps"
            hint="Serve per metterla sulla mappa e calcolare gli spostamenti"
            value={dove}
            onChange={(event) => {
              setDove(event.target.value);
              setPosizione(null);
              setAvviso(null);
            }}
          />
          <div className="mt-1 flex items-center gap-2">
            <Button
              variant="secondary"
              disabled={dove.trim().length < 3}
              loading={cerca.isPending}
              onClick={() => {
                cerca.mutate();
              }}
            >
              Trova sulla mappa
            </Button>
            {posizione !== null ? (
              <span className="text-[13px] text-[var(--budget-ok)]">Trovato</span>
            ) : null}
          </div>
        </div>

        {avviso !== null ? (
          <p className="rounded-[var(--r-sm)] bg-[var(--budget-warn-soft)] px-3 py-2 text-[13.5px] leading-5 text-[var(--budget-warn)]">
            {avviso}
          </p>
        ) : null}

        <div className="flex gap-3">
          <div className="flex-1">
            <TextField
              label="Dalle"
              type="time"
              value={ora}
              onChange={(event) => {
                setOra(event.target.value);
              }}
            />
          </div>
          <div className="flex-1">
            <TextField
              label="Per (minuti)"
              type="number"
              inputMode="numeric"
              min={5}
              max={1440}
              step={15}
              value={durata}
              onChange={(event) => {
                setDurata(event.target.value);
              }}
            />
          </div>
        </div>
      </div>

      {errore !== null ? (
        <p role="alert" className="mt-2 text-[13.5px] text-[var(--budget-blocked)]">
          {errore}
        </p>
      ) : null}

      <div className="mt-3 flex gap-2">
        <Button
          className="flex-1"
          disabled={!valido}
          loading={crea.isPending}
          onClick={() => {
            crea.mutate();
          }}
        >
          Aggiungi
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setAperto(false);
            setErrore(null);
          }}
        >
          Annulla
        </Button>
      </div>
    </Card>
  );
}

/**
 * Da «giorno + ora nel fuso della destinazione» all'istante assoluto.
 *
 * Senza questa conversione, chi organizza da casa una cena alle 20 a Chicago se
 * la ritroverebbe alle 13: il browser interpreterebbe l'ora nel proprio fuso.
 * Si calcola lo scarto vero fra i due fusi in quella data — non un numero fisso,
 * perché l'ora legale cambia durante l'anno.
 */
export function oraLocaleInIstante(data: string, ora: string, timezone: string): Date {
  const provvisorio = new Date(`${data}T${ora}:00Z`);

  const formattatore = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parti = Object.fromEntries(
    formattatore.formatToParts(provvisorio).map((parte) => [parte.type, parte.value]),
  );

  const comeLetta = Date.UTC(
    Number(parti['year']),
    Number(parti['month']) - 1,
    Number(parti['day']),
    Number(parti['hour']) === 24 ? 0 : Number(parti['hour']),
    Number(parti['minute']),
    Number(parti['second']),
  );

  return new Date(provvisorio.getTime() * 2 - comeLetta);
}
